/**
 * Presentation-time normalizer for legacy intake messages stored in the DB.
 *
 * IMPORTANT: this is a RENDER-TIME map only. The underlying DB content is
 * never mutated. User messages must be shown 1-on-1 without normalization.
 * Only system/JustEasySEO-authored messages pass through this helper so that
 * legacy English product names and stray markdown asterisks get cleaned
 * up in-view.
 */

// Bold markdown → HTML strong. Applied AFTER text-map substitutions.
function _boldMd(str) {
  return String(str).replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
}

// Case-insensitive word/phrase → NL replacements. Order matters: longer
// matches first so "Business Brain" wins over "business".
const SUBS = [
  [/\bBusiness Brain\b/g,      "Bedrijfskennis"],
  [/\bPrivacy Vault\b/g,       "Gegevens en koppelingen"],
  [/\bCommand Center\b/g,      "Overzicht"],
  [/\bMarketing Wallet\b/g,    "Marketingbudget"],
  [/\bGhost Mode\b/g,          "Adviesmodus"],
  [/\bGhost Ledger\b/g,        "Adviezen en besluiten"],
  [/\bMarket Radar\b/g,        "Marktoverzicht"],
  // Legacy intake wording — rewrite historical JustEasySEO messages in-place.
  [/\bURL van uw website\b/gi, "adres van uw website"],
  [/\btone[- ]?of[- ]?voice\b/gi, "schrijfstijl"],
  [/\bhoeveel en hoe agressief\b/gi, "welk budget en hoeveel risico"],
  [/Hoe agressief mag JustEasySEO opereren\??/gi,
                               "Hoe voorzichtig of ondernemend mag JustEasySEO adviseren?"],
  // Verkoopmodel — chat-echo mapping (B1-NL). Interne DB-waarde blijft
  // `B2B`/`B2C`/`B2B2C`/`Mixed`; alleen zichtbare weergave wordt vervangen.
  // Volgorde: lange varianten (B2B2C) eerst, dan B2B/B2C.
  [/\bB2B2C\b/g,               "via andere bedrijven aan particulieren"],
  [/\bB2B\b/g,                 "bedrijven"],
  [/\bB2C\b/g,                 "particulieren"],
  [/\bMixed\b/g,               "bedrijven én particulieren"],
  // Branche — chat-echo mapping (B1-NL). Interne code ongewijzigd.
  [/\bE-?commerce\b/gi,        "webwinkel"],
  [/\bRetail\b/g,              "winkels"],
  [/\bIT\/SaaS\b/gi,           "software en IT-diensten"],
  // Marketingdoel + zakelijk vocabulaire — chat-echo (M124-e).
  // Interne DB-waarde blijft de eerder opgeslagen chip-string; alleen
  // JustEasySEO-berichten worden bij weergave gemapt naar B1-NL.
  [/\bMeer leads\b/gi,         "Meer aanvragen"],
  [/\bLagere CAC\b/gi,         "Lagere kosten per nieuwe klant"],
  [/\bBetere ROAS\b/gi,        "Beter rendement op advertentie-uitgaven"],
  [/\bGrote enterprise trajecten\b/gi,
                                "Grote zakelijke opdrachten"],
  [/\btone[- ]?of[- ]?voice\b/gi, "schrijfstijl"],
  [/\bTone[- ]?of[- ]?[Vv]oice\b/g, "Schrijfstijl"],
  // M124-e: aanvullende B1-NL vervangingen — technische/Engelse termen
  // in JustEasySEO-echo naar klant-vriendelijke Nederlandse formuleringen.
  [/\bVERTROUWEN:\s*LOW\b/gi,     "Zekerheid: laag"],
  [/\bVERTROUWEN:\s*MEDIUM\b/gi,  "Zekerheid: middel"],
  [/\bVERTROUWEN:\s*HIGH\b/gi,    "Zekerheid: hoog"],
  [/\bvertrouwen:\s*low\b/g,      "zekerheid: laag"],
  [/\bvertrouwen:\s*medium\b/g,   "zekerheid: middel"],
  [/\bvertrouwen:\s*high\b/g,     "zekerheid: hoog"],
  [/\bSANDBOX\b/g,                "Testomgeving"],
  [/\bsandbox\b/g,                "testomgeving"],
  [/\bOrganische post\b/g,        "Gewone post"],
  [/\borganische post\b/g,        "gewone post"],
  [/\bSEO-artikel\b/g,            "Zoekmachine-artikel"],
  [/\bseo-artikel\b/gi,           "zoekmachine-artikel"],
  [/\bpubliceren komt later\b/gi, "publicatiestatus per kanaal"],
  [/\bEUR_USD_RATE(_SOURCE_URL)?\b/g,
                                   "wisselkoers-instelling (beheerder)"],
  [/\bbusiness\b/g,               "bedrijf"],
  [/\bBusiness\b/g,               "Bedrijf"],
];

/**
 * @param {string} text raw JustEasySEO/system message from DB
 * @returns {{ html: string, text: string }}
 */
export function normalizeIntakeMessage(text) {
  if (!text) return { html: "", text: "" };
  let out = String(text);
  for (const [re, rep] of SUBS) out = out.replace(re, rep);
  return { html: _boldMd(out), text: out.replace(/\*\*(.+?)\*\*/g, "$1") };
}

export default normalizeIntakeMessage;
